/**
 * DelegateQRDisplay - Display and print delegate QR code for check-ins
 * Uses qrcode.react to generate QR codes client-side
 */
import React, { useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  QrCode, Download, Printer, Share2, Copy, Check,
  User, Building2, Star, Mail, RefreshCw, AlertTriangle, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const DelegateQRDisplay = ({ 
  delegate, 
  eventId, 
  eventName = 'Event',
  showDialog = false,
  onClose 
}) => {
  const qrRef = useRef(null);
  const queryClient = useQueryClient();
  const [copied, setCopied] = React.useState(false);

  // Event branding drives the badge colours so screen and print match the PDF
  const { data: eventDoc } = useQuery({
    queryKey: ['event-branding', eventId],
    queryFn: () => api.get(`/admin/events/${eventId}`).then(r => r.data),
    enabled: !!eventId,
    staleTime: 60000,
  });
  const brandPrimary = eventDoc?.branding?.primary_color || '#3B82F6';
  const brandSecondary = eventDoc?.branding?.secondary_color || '#1E40AF';
  const badgeGradient = `linear-gradient(180deg, ${brandPrimary} 0%, ${brandSecondary} 100%)`;
  
  // Fetch QR health status
  const { data: qrDebug, isLoading: isLoadingDebug } = useQuery({
    queryKey: ['qr-debug', eventId, delegate?.id],
    queryFn: async () => {
      const response = await api.get(`/api/events/${eventId}/delegates/${delegate.id}/qr-debug`);
      return response.data;
    },
    enabled: showDialog && !!delegate?.id && !!eventId,
    staleTime: 30000
  });

  // Regenerate QR mutation
  const regenerateMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/api/events/${eventId}/delegates/${delegate.id}/regenerate-qr`);
      return response.data;
    },
    onSuccess: (data) => {
      toast.success('QR code regenerated');
      queryClient.invalidateQueries(['qr-debug', eventId, delegate?.id]);
      queryClient.invalidateQueries(['delegates', eventId]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to regenerate QR');
    }
  });
  
  // Generate QR data - this format matches backend expectation
  const qrData = delegate?.qr_data || 
    `EVTDLG:${eventId}:${delegate?.id}:${delegate?.badge_code || delegate?.id}`;
  
  const delegateName = delegate?.name || 
    `${delegate?.first_name || ''} ${delegate?.last_name || ''}`.trim() || 
    'Delegate';

  // Check QR health
  const hasIssues = qrDebug?.qr_debug?.issues?.length > 0 || qrDebug?.qr_debug?.needs_regeneration;
  const qrFormat = qrDebug?.qr_debug?.detected_format || 'unknown';

  // Download QR as PNG
  const downloadQR = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) {
      toast.error('QR code not ready. Please wait a moment and try again.');
      return;
    }
    
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const svgData = new XMLSerializer().serializeToString(svg);
      const img = new Image();
      
      canvas.width = 400;
      canvas.height = 400;
      
      img.onload = () => {
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, 400, 400);
        
        const link = document.createElement('a');
        link.download = `qr-${delegateName.replace(/\s+/g, '-').toLowerCase()}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
        toast.success('QR code downloaded!');
      };
      
      img.onerror = () => {
        toast.error('Failed to generate QR image');
      };
      
      img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Failed to download QR code');
    }
  };

  // Print QR code
  const printQR = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) {
      toast.error('QR code not ready. Please wait a moment and try again.');
      return;
    }
    
    try {
      const svgData = new XMLSerializer().serializeToString(svg);
      
      // Create print content
      const printContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>QR Code - ${delegateName}</title>
            <style>
              body {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                min-height: 100vh;
                margin: 0;
                padding: 20px;
                box-sizing: border-box;
              }
              .badge-card {
                border: 2px solid #e5e7eb;
                border-radius: 16px;
                padding: 24px;
                text-align: center;
                max-width: 350px;
                background: ${badgeGradient};
                color: white;
              }
              .name { font-size: 24px; font-weight: bold; margin-bottom: 8px; }
              .title { font-size: 14px; opacity: 0.9; margin-bottom: 4px; }
              .company { font-size: 16px; margin-bottom: 16px; }
              .qr-container {
                background: white;
                padding: 16px;
                border-radius: 12px;
                display: inline-block;
                margin: 16px 0;
              }
              .qr-container svg {
                display: block;
                width: 200px;
                height: 200px;
              }
              .tier-badge {
                display: inline-block;
                background: rgba(255,255,255,0.2);
                padding: 6px 16px;
                border-radius: 20px;
                font-size: 14px;
                font-weight: 600;
              }
              .event-name { font-size: 12px; opacity: 0.7; margin-top: 16px; }
              .badge-code { font-size: 11px; opacity: 0.6; margin-top: 8px; font-family: monospace; }
              @media print {
                body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              }
            </style>
          </head>
          <body>
            <div class="badge-card">
              <div class="name">${delegateName}</div>
              ${delegate?.title ? `<div class="title">${delegate.title}</div>` : ''}
              ${delegate?.company ? `<div class="company">${delegate.company}</div>` : ''}
              <div class="qr-container">
                ${svgData}
              </div>
              <div class="tier-badge">
                ${delegate?.is_vip ? '⭐ VIP' : (delegate?.tier_name || delegate?.tier?.name || 'Standard')}
              </div>
              <div class="event-name">${eventName}</div>
              ${delegate?.badge_code ? `<div class="badge-code">Badge: ${delegate.badge_code}</div>` : ''}
            </div>
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                }, 500);
              };
            </script>
          </body>
        </html>
      `;
      
      const printWindow = window.open('', '_blank', 'width=450,height=600');
      if (!printWindow) {
        toast.error('Please allow popups to print. Check your browser settings.');
        return;
      }
      
      printWindow.document.write(printContent);
      printWindow.document.close();
      toast.success('Print dialog opened');
    } catch (err) {
      console.error('Print error:', err);
      toast.error('Failed to print QR code');
    }
  };

  // Copy QR data
  const copyQRData = () => {
    navigator.clipboard.writeText(qrData).then(() => {
      setCopied(true);
      toast.success('QR data copied!');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Share QR as image (if supported)
  const shareQR = async () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) {
      toast.error('QR code not ready. Please wait a moment and try again.');
      return;
    }

    try {
      // Convert SVG to PNG blob for sharing
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = 400;
      canvas.height = 500; // Extra height for text
      
      const img = new Image();
      const svgData = new XMLSerializer().serializeToString(svg);
      
      // Wait for image to load
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = () => reject(new Error('Failed to load QR image'));
        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
      });
      
      // Draw badge card background
      ctx.fillStyle = '#3B82F6';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw name
      ctx.fillStyle = 'white';
      ctx.font = 'bold 24px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(delegateName, canvas.width / 2, 40);
      
      // Draw company if exists
      if (delegate?.company) {
        ctx.font = '16px Arial, sans-serif';
        ctx.fillText(delegate.company, canvas.width / 2, 65);
      }
      
      // Draw white background for QR
      ctx.fillStyle = 'white';
      ctx.fillRect(100, 80, 200, 200);
      
      // Draw QR code
      ctx.drawImage(img, 100, 80, 200, 200);
      
      // Draw event name
      ctx.fillStyle = 'white';
      ctx.font = '14px Arial, sans-serif';
      ctx.fillText(eventName, canvas.width / 2, 310);
      
      // Draw badge code
      if (delegate?.badge_code) {
        ctx.font = '12px Arial, sans-serif';
        ctx.fillText(`Badge: ${delegate.badge_code}`, canvas.width / 2, 335);
      }
      
      // Draw tier
      const tierText = delegate?.is_vip ? 'VIP' : (delegate?.tier_name || delegate?.tier?.name || 'Standard');
      ctx.font = 'bold 14px Arial, sans-serif';
      ctx.fillText(tierText, canvas.width / 2, 360);
      
      // Convert canvas to blob
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((b) => {
          if (b) resolve(b);
          else reject(new Error('Failed to create image'));
        }, 'image/png');
      });
      
      const fileName = `badge-${delegateName.replace(/\s+/g, '-').toLowerCase()}.png`;
      const file = new File([blob], fileName, { type: 'image/png' });
      
      // Try file sharing first (works on mobile)
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: `Event Badge - ${delegateName}`,
          text: `${eventName}\nBadge for ${delegateName}${delegate?.badge_code ? `\nCode: ${delegate.badge_code}` : ''}`,
          files: [file]
        });
        toast.success('Badge shared!');
      } else if (navigator.share) {
        // Text-only share fallback
        await navigator.share({
          title: `Event Badge - ${delegateName}`,
          text: `${eventName}\nBadge for ${delegateName}\nCheck-in Code: ${delegate?.badge_code || qrData}`
        });
        toast.success('Badge info shared!');
      } else {
        // Desktop fallback - download the image instead
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(url);
        toast.success('Badge image downloaded!');
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        // User cancelled - do nothing
        return;
      }
      console.error('Share failed:', err);
      // Final fallback - copy QR data
      copyQRData();
      toast.info('QR data copied to clipboard');
    }
  };

  const QRContent = () => (
    <div className="space-y-4">
      {/* QR Health Status */}
      {!isLoadingDebug && qrDebug && (
        <div className={`flex items-center justify-between p-2 rounded-lg text-sm ${
          hasIssues 
            ? 'bg-yellow-50 border border-yellow-200' 
            : 'bg-green-50 border border-green-200'
        }`}>
          <div className="flex items-center gap-2">
            {hasIssues ? (
              <>
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
                <span className="text-yellow-700">QR needs attention</span>
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-green-700">QR code is valid</span>
              </>
            )}
          </div>
          <Badge variant="outline" className="text-xs">
            {qrFormat}
          </Badge>
        </div>
      )}

      {/* Badge Preview */}
      <div 
        className="rounded-xl p-6 text-white text-center"
        data-testid="delegate-badge-card"
        style={{ background: badgeGradient }}
      >
        <div className="text-xl font-bold mb-1">{delegateName}</div>
        {delegate?.title && (
          <div className="text-sm opacity-90 mb-1">{delegate.title}</div>
        )}
        {delegate?.company && (
          <div className="font-medium flex items-center justify-center gap-1">
            <Building2 className="h-4 w-4" />
            {delegate.company}
          </div>
        )}
        
        {/* QR Code - High contrast for better scanning */}
        <div 
          ref={qrRef}
          className="bg-white p-4 rounded-lg inline-block my-4 relative"
        >
          <QRCodeSVG 
            value={qrData}
            size={200}
            level="H"
            includeMargin={true}
            bgColor="#ffffff"
            fgColor="#000000"
          />
          {hasIssues && (
            <div className="absolute -top-2 -right-2 bg-yellow-500 text-white rounded-full p-1">
              <AlertTriangle className="h-4 w-4" />
            </div>
          )}
        </div>
        
        {/* Tier Badge */}
        <div className="inline-flex items-center gap-1 bg-white/20 px-4 py-1.5 rounded-full text-sm font-semibold">
          {delegate?.is_vip && <Star className="h-4 w-4 text-yellow-300" />}
          {delegate?.is_vip ? 'VIP' : (delegate?.tier_name || delegate?.tier?.name || 'Standard')}
        </div>
        
        <div className="text-xs mt-4 opacity-70">
          Scan for check-in • {eventName}
        </div>
      </div>

      {/* QR Issues Details */}
      {hasIssues && qrDebug?.qr_debug?.issues?.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm">
          <div className="font-medium text-yellow-800 mb-1">Issues found:</div>
          <ul className="text-yellow-700 text-xs space-y-1">
            {qrDebug.qr_debug.issues.map((issue, idx) => (
              <li key={idx} className="flex items-start gap-1">
                <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                {issue.message}
              </li>
            ))}
          </ul>
          <Button 
            size="sm" 
            variant="outline"
            className="mt-2 w-full border-yellow-400 text-yellow-700 hover:bg-yellow-100"
            onClick={() => regenerateMutation.mutate()}
            disabled={regenerateMutation.isPending}
          >
            {regenerateMutation.isPending ? (
              <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-1" />
            )}
            Fix QR Code
          </Button>
        </div>
      )}
      
      {/* Delegate Info */}
      <div className="grid grid-cols-2 gap-2 text-sm">
        {delegate?.email && (
          <div className="flex items-center gap-2 text-slate-600 col-span-2">
            <Mail className="h-4 w-4" />
            {delegate.email}
          </div>
        )}
        {delegate?.badge_code && (
          <div className="text-slate-500">
            <span className="text-xs text-slate-400">Badge Code:</span>
            <br />
            <code className="text-xs bg-slate-100 px-2 py-0.5 rounded">
              {delegate.badge_code}
            </code>
          </div>
        )}
      </div>
      
      {/* Actions */}
      <div className="flex gap-2 flex-wrap justify-center">
        <Button onClick={downloadQR} variant="outline" size="sm">
          <Download className="h-4 w-4 mr-1" />
          Download
        </Button>
        <Button onClick={printQR} variant="outline" size="sm">
          <Printer className="h-4 w-4 mr-1" />
          Print
        </Button>
        <Button onClick={copyQRData} variant="outline" size="sm">
          {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
          {copied ? 'Copied!' : 'Copy'}
        </Button>
        {navigator.share && (
          <Button onClick={shareQR} variant="outline" size="sm">
            <Share2 className="h-4 w-4 mr-1" />
            Share
          </Button>
        )}
      </div>
      
      {/* Regenerate button for manual refresh */}
      {!hasIssues && (
        <div className="text-center">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => regenerateMutation.mutate()}
            disabled={regenerateMutation.isPending}
            className="text-xs text-slate-500"
          >
            {regenerateMutation.isPending ? (
              <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
            ) : (
              <RefreshCw className="h-3 w-3 mr-1" />
            )}
            Regenerate QR
          </Button>
        </div>
      )}
    </div>
  );

  // If used as a dialog trigger
  if (showDialog) {
    return (
      <Dialog open={showDialog} onOpenChange={onClose}>
        <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              Event Badge
            </DialogTitle>
          </DialogHeader>
          <QRContent />
        </DialogContent>
      </Dialog>
    );
  }

  // Standalone card view
  return (
    <Card className="max-w-sm" data-testid="delegate-qr-display">
      <CardHeader className="text-center pb-2">
        <CardTitle className="flex items-center justify-center gap-2">
          <QrCode className="h-5 w-5" />
          Event Badge
        </CardTitle>
      </CardHeader>
      <CardContent>
        <QRContent />
      </CardContent>
    </Card>
  );
};

export default DelegateQRDisplay;
